import { PAYMENT_STATE_LABELS } from '@repo/types';
import { PAYMENT_STATE } from '@theailab-dev/parchment';
import type { PaymentState } from '@theailab-dev/parchment';
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DetailPageLayout } from '../../components/Layout/DetailPageLayout';
import { Badge, Button, Card, InfoItem } from '../../components/ui';
import ConfirmModal from '../../components/ui/ConfirmModal';
import { usePaymentDetail } from '../../hooks/usePaymentDetail';

const PaymentDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const paymentId = id ? Number(id) : NaN;

  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showRefundModal, setShowRefundModal] = useState(false);

  const { data, isLoading, isError, error, refetch } = usePaymentDetail(Number.isNaN(paymentId) ? null : paymentId);

  // 디버깅용 로그
  if (data) {
    console.log('[PaymentDetail] data', data);
  }

  const formatDateTime = (date: Date | string | null | undefined) => {
    if (!date) return '-';
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(dateObj.getTime())) return '-';

      return dateObj.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return '-';
    }
  };

  const getStateBadgeVariant = (state: PaymentState) => {
    switch (state) {
      case PAYMENT_STATE.SUCCEEDED:
        return 'success';
      case PAYMENT_STATE.PROCESSING:
        return 'primary';
      case PAYMENT_STATE.FAILED:
        return 'danger';
      case PAYMENT_STATE.READY:
      default:
        return 'neutral';
    }
  };

  const formatAmount = (value: number | string | null | undefined) => {
    if (value === null || value === undefined) return '-';

    const num = typeof value === 'number' ? value : Number.isNaN(Number(value)) ? NaN : Number(value);

    if (!Number.isFinite(num)) return '-';
    return num.toLocaleString();
  };

  const handleGoOrderSheet = () => {
    if (!data) return;
    // 주문서 상세 화면으로 이동 (결제 상세에서 왔다는 정보 전달)
    navigate(`/dashboard/order-sheets/${data.orderSheetId}`, {
      state: { from: `/dashboard/payments/${paymentId}` },
    });
  };

  const handleGoProductDetail = (productId: number) => {
    // 결제 상세 화면으로 돌아올 수 있도록 location.state에 현재 경로 전달
    navigate(`/dashboard/products/${productId}`, {
      state: { from: `/dashboard/payments/${paymentId}` },
    });
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      // 복사 성공 피드백 (선택적)
    });
  };

  return (
    <DetailPageLayout
      title="결제 상세"
      subtitle={data ? `결제 ID: ${data.id} · 주문서 ID: ${data.orderSheetId}` : undefined}
      isLoading={isLoading}
      isError={isError}
      errorMessage={error instanceof Error ? error.message : undefined}
      onRetry={refetch}>
      {data && (
        <div className="space-y-6">
          {/* 결제 정보 카드 */}
          <Card
            title="결제 정보"
            actions={
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" onClick={handleGoOrderSheet}>
                  주문서 상세로 이동
                </Button>
                {data.state === PAYMENT_STATE.SUCCEEDED && (
                  <Button size="sm" variant="danger" onClick={() => setShowRefundModal(true)}>
                    환불 처리
                  </Button>
                )}
              </div>
            }>
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <InfoItem label="결제 ID" value={`#${data.id}`} />
              <InfoItem label="주문서 ID" value={`#${data.orderSheetId}`} />
              <InfoItem
                label="결제 상태"
                value={
                  <Badge variant={getStateBadgeVariant(data.state)} className="whitespace-nowrap">
                    {PAYMENT_STATE_LABELS[data.state]}
                  </Badge>
                }
              />
              <InfoItem label="결제 수단" value={data.method} />
              <InfoItem label="결제 금액" value={`${formatAmount(data.amount)}원`} />
              <InfoItem label="결제일시" value={formatDateTime(data.createdAt)} />
              <InfoItem label="승인 번호" value={data.applyNum || '-'} />
              <InfoItem
                label="영수증"
                value={
                  data.receiptUrl ? (
                    <Button size="sm" variant="outline" onClick={() => setShowReceiptModal(true)}>
                      영수증 보기
                    </Button>
                  ) : (
                    '-'
                  )
                }
              />
              {/* 실패 사유 */}
              {data.state === PAYMENT_STATE.FAILED && (
                <>
                  {data.state === PAYMENT_STATE.FAILED && data.failureReason && (
                    <InfoItem label="실패 사유" value={<span className="text-red-600">{data.failureReason}</span>} />
                  )}
                </>
              )}
            </div>

            {/* PG 상세 정보 */}
            {(data.pgProvider || data.pgTid || data.impUid) && (
              <div className="mt-6 border-t border-slate-200 pt-6">
                <div className="mb-3 text-sm font-medium text-slate-700">PG 상세 정보</div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  {data.impUid && (
                    <div>
                      <label className="mb-1 block text-xs text-slate-500">포트원 거래번호 (imp_uid)</label>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-slate-900">{data.impUid}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCopyToClipboard(data.impUid!)}
                          className="h-6 px-2 text-xs">
                          복사
                        </Button>
                      </div>
                    </div>
                  )}
                  {data.pgProvider && (
                    <div>
                      <label className="mb-1 block text-xs text-slate-500">PG사 구분 코드</label>
                      <span className="text-sm text-slate-900">{data.pgProvider}</span>
                    </div>
                  )}
                  {data.pgTid && (
                    <div>
                      <label className="mb-1 block text-xs text-slate-500">PG 거래 번호</label>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-slate-900">{data.pgTid}</span>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleCopyToClipboard(data.pgTid!)}
                          className="h-6 px-2 text-xs">
                          복사
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </Card>

          {/* 영수증 모달 - 보안 설정(X-Frame-Options) 때문에 iframe 대신 새 창 안내만 표시 */}
          {data?.receiptUrl && (
            <ConfirmModal
              isOpen={showReceiptModal}
              onClose={() => setShowReceiptModal(false)}
              type="info"
              title="영수증"
              message={
                <div className="space-y-3">
                  <p className="text-sm text-slate-600">
                    보안 정책으로 인해 이 페이지 안에서는 영수증 화면을 바로 표시할 수 없습니다.
                  </p>
                  <p className="text-sm text-slate-600">아래 버튼을 눌러 새 창에서 영수증을 확인해주세요.</p>
                </div>
              }
              buttons={[
                {
                  label: '새 창에서 열기',
                  onClick: () => {
                    window.open(data.receiptUrl!, '_blank', 'noopener,noreferrer');
                  },
                  variant: 'primary',
                },
                {
                  label: '닫기',
                  onClick: () => setShowReceiptModal(false),
                  variant: 'secondary',
                },
              ]}
            />
          )}

          {/* 상품 목록 */}
          <Card title={`구매한 상품 (${data.products.length})`}>
            {data.products.length === 0 ? (
              <p className="text-sm text-slate-500">이 결제로 연관된 상품이 없습니다.</p>
            ) : (
              <div className="divide-y divide-slate-100">
                {data.products.map((product) => (
                  <div key={product.productId} className="flex items-center justify-between py-4">
                    <div className="flex items-center gap-4">
                      {product.imageUrl && (
                        <img
                          src={product.imageUrl}
                          alt={product.title}
                          className="h-14 w-14 rounded-lg border border-slate-200 object-cover"
                        />
                      )}
                      <div>
                        <div className="text-sm font-semibold text-slate-900">{product.title}</div>
                        <div className="mt-1 text-xs text-slate-500">
                          {formatAmount(product.originalPrice)}원
                          {product.discount != null && Number(product.discount) > 0 && (
                            <span className="ml-1 text-red-500">(-{formatAmount(product.discount)}원)</span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => handleGoProductDetail(product.productId)}>
                      상품 상세
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}

      {/* 환불 처리 확인 모달 */}
      {data && (
        <ConfirmModal
          isOpen={showRefundModal}
          onClose={() => setShowRefundModal(false)}
          type="warning"
          title="환불 처리 확인"
          message={
            <div className="space-y-3">
              <p className="text-sm text-slate-600">정말로 이 결제를 환불 처리하시겠습니까?</p>
              <div className="space-y-2 rounded-lg bg-slate-50 p-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">결제 ID:</span>
                  <span className="font-semibold text-slate-900">#{data.id}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">결제 금액:</span>
                  <span className="font-semibold text-slate-900">{formatAmount(data.amount)}원</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">결제 수단:</span>
                  <span className="font-semibold text-slate-900">{data.method}</span>
                </div>
              </div>
              <p className="mt-2 text-xs text-red-600">⚠️ 환불 처리 후에는 되돌릴 수 없습니다.</p>
            </div>
          }
          buttons={[
            {
              label: '취소',
              onClick: () => setShowRefundModal(false),
              variant: 'secondary',
            },
            {
              label: '환불 처리',
              onClick: () => {
                // TODO: 환불 처리 API 호출
                console.log('환불 처리 시작', data.id);
                setShowRefundModal(false);
                // 추후 구현: 환불 처리 API 호출 및 상태 업데이트
              },
              variant: 'danger',
            },
          ]}
        />
      )}
    </DetailPageLayout>
  );
};

export default PaymentDetail;
